/* eslint-disable react-refresh/only-export-components --
   The hook and the one control that drives it are a single concern: what hides
   the chrome, and the button a cook uses to get back out of it. Splitting them
   would put half of station mode in a module and half in whichever screen
   imported it, which is the opposite of what this file is for. */
import { useCallback, useEffect, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Maximize2, Minimize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { useDialogEscape } from '@/components/ui/dialog';

/**
 * ## A screen that is set up once and left running
 *
 * The kitchen board on a wall, the waiter station at the pass, the kids desk on
 * a counter. On all three the dashboard around the screen — rail, header,
 * branch switcher, page padding — is chrome for someone driving a mouse, and on
 * a display nobody touches between service and closing it is roughly 300px of
 * the screen spent on controls that are never used.
 *
 * ### Two mechanisms, and why both
 *
 * The browser's Fullscreen API hides the *browser's* own chrome — tab strip,
 * address bar — which is the half an in-app mode cannot reach and exactly what
 * a wall screen wants. It is also the half that cannot be relied on: it needs a
 * user gesture, so a reload can never restore it; a permissions policy or an
 * iframe can refuse it; iOS Safari has no element fullscreen at all; and it
 * ends on its own for reasons that have nothing to do with this app (F11, a
 * browser update prompt, the OS switching spaces).
 *
 * So the mode is an in-app one — a fixed overlay the page draws itself — and
 * the API is layered on top as an enhancement. Everything that decides what is
 * on screen reads {@link StationMode.active}; `requestFullscreen` is fired and
 * forgotten. A refusal costs the browser chrome and nothing else.
 *
 * ### Leaving
 *
 * Three ways out, all ending in the same state:
 *  - the button, the discoverable one and the only one that matters to somebody
 *    who did not set the screen up — it is on screen the whole time;
 *  - Escape, through `useDialogEscape` rather than a listener of this hook's
 *    own. A dialog opened on top of the mode owns Escape while it is up, and a
 *    second document listener here would close the dialog *and* drop the mode
 *    on one press — which is the bug that stack exists to prevent;
 *  - the browser leaving fullscreen without being asked. The mode follows it
 *    out, because the alternative is a page with no sidebar, no address bar and
 *    no visible reason it looks like that.
 *
 * ### Coming back
 *
 * The flag is persisted, because a browser that restarts overnight must come
 * back to the board rather than to a dashboard nobody is in front of. What is
 * *not* restored is the browser fullscreen: there is no gesture on a reload, so
 * it cannot be — and that is the safe half, since the mode a reload restores is
 * the in-app one, which always carries its own visible exit.
 */
export interface StationMode {
  /** Whether the screen is currently drawn as a station display. */
  active: boolean;
  enter: () => void;
  exit: () => void;
  toggle: () => void;
  /**
   * Classes for the screen's outermost element. Off, this is the ordinary page
   * spacing; on, the screen covers the viewport and paints its own ground,
   * which is what hides the rail and the header without either of them knowing.
   *
   * A screen that needs a different overlay — the kitchen board sticks its
   * station bar to the scrollport, so it cannot afford top padding — reads
   * {@link active} and writes its own instead.
   */
  shellClassName: string;
  /** Goes on the element the mode draws. It is what orders Escape correctly. */
  ref: RefObject<HTMLDivElement | null>;
}

export interface StationModeOptions {
  /**
   * Whether there is anything to show a station display *of*. The kitchen board
   * with no station picked is a setup screen and a bad place to be locked into
   * a mode, so entering is refused and a restored flag is dropped.
   */
  available?: boolean;
  /**
   * Root font-size while the mode is up, for a screen read from metres rather
   * than from a desk. Every size in this app is rem-based, so one root font-size
   * reflows the whole scale — as opposed to a `transform: scale`, which blurs
   * the text and leaves the layout at its old width.
   *
   * On the root rather than the overlay because the overlay is `position:
   * fixed`, and a font-size on a fixed element sizes the element as well as its
   * contents. The app's chrome scales too, under the overlay, unseen.
   */
  rootFontSize?: string;
}

type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};

type FullscreenRoot = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

function fullscreenElement(): Element | null {
  const doc = document as FullscreenDocument;
  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

/**
 * The document element rather than the overlay: the overlay is a React-owned
 * node, and a fullscreen element that unmounts drops fullscreen with it.
 */
async function enterBrowserFullscreen(): Promise<void> {
  const root = document.documentElement as FullscreenRoot;
  const request = root.requestFullscreen ?? root.webkitRequestFullscreen;
  if (!request) return;
  try {
    await request.call(root);
  } catch {
    // Refused — a permissions policy, a gesture the browser did not count, or a
    // platform that has no fullscreen. The in-app mode is already up.
  }
}

function leaveBrowserFullscreen(): void {
  if (!fullscreenElement()) return;
  const doc = document as FullscreenDocument;
  const exit = doc.exitFullscreen ?? doc.webkitExitFullscreen;
  try {
    void exit?.call(doc);
  } catch {
    // Nothing to do about it, and the app's own mode has already been left.
  }
}

function stored(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    // Private mode, or storage disabled. The screen still works; it just does
    // not remember, which is better than not rendering.
    return false;
  }
}

function store(key: string, on: boolean): void {
  try {
    if (on) localStorage.setItem(key, '1');
    else localStorage.removeItem(key);
  } catch {
    // As above.
  }
}

const OFF = 'space-y-4';
const ON = 'fixed inset-0 z-40 space-y-4 overflow-y-auto overscroll-contain bg-page p-4';

/**
 * @param key where the flag lives. One per screen, so a terminal left in station
 * mode on the pass does not put the kitchen board into it too.
 */
export function useStationMode(key: string, options: StationModeOptions = {}): StationMode {
  const { available = true, rootFontSize } = options;
  const [active, setActive] = useState(() => available && stored(key));

  const exit = useCallback(() => {
    setActive(false);
    store(key, false);
    leaveBrowserFullscreen();
  }, [key]);

  const enter = useCallback(() => {
    setActive(true);
    store(key, true);
    // Fired from the click that set the flag above, which is the gesture the
    // API requires. Deliberately not awaited: what is on screen does not depend
    // on the answer.
    void enterBrowserFullscreen();
  }, [key]);

  const toggle = useCallback(() => (active ? exit() : enter()), [active, enter, exit]);

  useEffect(() => {
    if (!available) exit();
  }, [available, exit]);

  useEffect(() => {
    if (!active || !rootFontSize) return;
    const root = document.documentElement;
    const previous = root.style.fontSize;
    root.style.fontSize = rootFontSize;
    return () => {
      root.style.fontSize = previous;
    };
  }, [active, rootFontSize]);

  /**
   * Fullscreen that ends without this app ending it.
   *
   * `had` is what keeps this honest. Without it, any `fullscreenchange` on the
   * page — a video player closing, in a mode entered while the API was refused —
   * would read as "the browser left" and drop the mode for no reason.
   */
  useEffect(() => {
    if (!active) return;
    let had = fullscreenElement() !== null;
    const onChange = () => {
      if (fullscreenElement()) {
        had = true;
        return;
      }
      if (had) exit();
    };
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, [active, exit]);

  /**
   * A wall screen that dims after ninety seconds is a board nobody can read,
   * and the failure looks exactly like a quiet kitchen. The lock dies silently
   * whenever the tab is hidden and is never re-issued on its own, so the
   * visibility listener is not optional — a half-working wake lock is worse
   * than none, because it is trusted.
   */
  useEffect(() => {
    if (!active || !navigator.wakeLock) return;
    let sentinel: WakeLockSentinel | null = null;
    let done = false;

    const acquire = async () => {
      try {
        const next = await navigator.wakeLock.request('screen');
        if (done) void next.release().catch(() => undefined);
        else sentinel = next;
      } catch {
        // Battery saver, a background tab, or a browser without it. The screen
        // is unchanged; only its timeout is.
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible' && !done) void acquire();
    };

    void acquire();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      done = true;
      document.removeEventListener('visibilitychange', onVisible);
      void sentinel?.release().catch(() => undefined);
    };
  }, [active]);

  /**
   * Navigating away — browser back, a keyboard shortcut — must not leave the
   * rest of the dashboard in browser fullscreen with no visible way out. The
   * persisted flag is left alone: this is still a station screen, and coming
   * back to it should come back to the mode.
   */
  useEffect(() => () => leaveBrowserFullscreen(), []);

  const ref = useDialogEscape(exit, active);

  return { active, enter, exit, toggle, shellClassName: active ? ON : OFF, ref };
}

/**
 * One button, both directions, and never an icon on its own: the person who has
 * to find the way out did not set this screen up and has no reason to know that
 * Escape does anything — which is what `aria-keyshortcuts` tells the people who
 * would otherwise never be told.
 */
export function StationModeToggle({ mode }: { mode: StationMode }) {
  const { t } = useTranslation();
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={mode.toggle}
      aria-pressed={mode.active}
      aria-keyshortcuts={mode.active ? 'Escape' : undefined}
    >
      <Icon as={mode.active ? Minimize2 : Maximize2} />
      {t(mode.active ? 'operations.station.exitStationMode' : 'operations.station.stationMode')}
    </Button>
  );
}
